import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';

import { PopUpService } from './popup.service';

import { IMapConfigForModule, ISelectedMapOptions } from '~/repositories/search/store/search.types';
import { convertToGeoJson } from '~/utils/convert-to-geojson';
@Component({
  selector: 'app-results-map',
  templateUrl: './results-map.component.html',
  styleUrl: './results-map.component.scss',
  standalone: false,
  providers: [PopUpService]
})
export class ResultsMapComponent implements OnChanges {
  @Input() mapOptions: IMapConfigForModule;
  @Input() currentMapOptions: ISelectedMapOptions;
  @Input() showOptions: boolean = true;
  @Input() results: any[];
  @Output() currentMapOptionsChange: EventEmitter<{ mapOptions: ISelectedMapOptions; doSearch: boolean }> =
    new EventEmitter();

  currentZoomLevel = signal<number>(7);

  options: L.MapOptions = {
    layers: [
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 20,
        minZoom: 5,
        attribution: ''
      })
    ],
    zoom: 7,
    center: L.latLng(50.6402809, 4.6667145)
  };

  markerClusterData: L.Layer[] = [];
  markerClusterOptions: L.MarkerClusterGroupOptions = {
    iconCreateFunction: (cluster) => {
      return L.divIcon({
        html: `${cluster.getChildCount()}`,
        className: 'marker-cluster-custom',
        iconSize: L.point(40, 40)
      });
    }
  };

  private map: L.Map;

  constructor(private popupService: PopUpService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] && this.results?.length) {
      this.updateMarkers();
    }
    if (changes['currentMapOptions'] && this.currentMapOptions) {
      this.updateMapOptions();
    }
  }

  private updateMarkers(): void {
    this.markerClusterData = this.createMarkers();
  }

  private updateMapOptions(): void {
    if (this.currentMapOptions?.startingPoint?.length) {
      this.options.center = L.latLng(this.currentMapOptions.startingPoint[0], this.currentMapOptions.startingPoint[1]);
    }
    if (this.currentMapOptions?.zoomLevel) {
      this.options.zoom = this.currentMapOptions.zoomLevel;
      this.currentZoomLevel.set(this.currentMapOptions.zoomLevel);
    }
  }

  private createMarkers(): L.Layer[] {
    const markers: L.Layer[] = [];
    const geoJsonData = convertToGeoJson(this.results);

    const defaultIcon = L.icon({
      iconUrl: 'assets/marker.png',
      iconSize: [30, 49],
      iconAnchor: [15, 30],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28]
    });

    const polygonIcon = L.icon({
      iconUrl: 'assets/circle-marker.png',
      iconSize: [29, 29],
      iconAnchor: [15, 29],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28]
    });

    for (const feature of geoJsonData.features) {
      const coords = (feature.geometry as any).coordinates;
      let marker: L.Marker;

      if (feature.geometry.type === 'Polygon') {
        const center = L.geoJson(feature).getBounds().getCenter();
        marker = L.marker(center, { icon: polygonIcon });
        marker.bindPopup(this.popupService.compilePopup(feature.properties));
      } else if (coords && Array.isArray(coords[0]) && coords[0].length >= 2) {
        const [lat, lng] = coords[0];
        marker = L.marker([lat, lng], { icon: defaultIcon });
        marker.bindPopup(this.popupService.compilePopup(feature));
      } else {
        continue;
      }

      markers.push(marker);
    }

    return markers;
  }

  onMapReady(map: L.Map): void {
    this.map = map;

    map.on('zoomend', () => {
      this.currentZoomLevel.set(map.getZoom());
      this.changeMapOptions('zoomLevel', map.getZoom(), false);
    });

    map.on('moveend', () => {
      this.changeMapOptions('startingPoint', [map.getCenter().lat, map.getCenter().lng], false);
    });

    if (this.currentMapOptions?.zoomLevel === null || this.currentMapOptions?.startingPoint === null) {
      this.handleResetZoomLevel();
    }
  }

  changeMapOptions(key: string, value: any, doSearch: boolean) {
    this.currentMapOptions = {
      ...this.currentMapOptions,
      [key]: value
    };

    this.currentMapOptionsChange.emit({
      mapOptions: this.currentMapOptions,
      doSearch: doSearch
    });
  }

  handleSelectCurrentLocation() {
    if (this.map) {
      this.map.locate({ setView: true, maxZoom: this.currentZoomLevel() });
    }
  }

  handleResetZoomLevel() {
    if (this.map && this.markerClusterData.length) {
      const group = L.featureGroup(this.markerClusterData);
      this.map.fitBounds(group.getBounds());
      this.currentZoomLevel.set(this.map.getZoom());
    }
  }
}
